import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, LogOut, BarChart3, Receipt, Crown, Tag, Settings, MessageSquare, HelpCircle, FileText, Calculator } from 'lucide-react';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <Receipt className="w-8 h-8 text-indigo-400" />
            <span className="text-2xl font-bold font-['Outfit'] bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
              FinScan USA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to="/dashboard"
              data-testid="nav-dashboard"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/transactions"
              data-testid="nav-transactions"
              className="text-muted-foreground hover:text-white transition-colors"
            >
              Transactions
            </Link>
            <Link
              to="/analytics"
              data-testid="nav-analytics"
              className="text-muted-foreground hover:text-white transition-colors"
            >
              Analytics
            </Link>
            <Link
              to="/invoices"
              data-testid="nav-invoices"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
            >
              <FileText className="w-4 h-4" />
              <span>Invoices</span>
            </Link>
            <Link
              to="/tax-calculator"
              data-testid="nav-tax-calculator"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
            >
              <Calculator className="w-4 h-4" />
              <span>Taxes</span>
            </Link>
            <Link
              to="/smart-rules"
              data-testid="nav-smart-rules"
              className="text-muted-foreground hover:text-white transition-colors"
            >
              Smart Rules
            </Link>
            <Link
              to="/receipts-gallery"
              data-testid="nav-receipts-gallery"
              className="text-muted-foreground hover:text-white transition-colors"
            >
              Receipts
            </Link>
            <Link
              to="/budget-limits"
              data-testid="nav-budget-limits"
              className="text-muted-foreground hover:text-white transition-colors"
            >
              Budgets
            </Link>
            <Link
              to="/categories"
              data-testid="nav-categories"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
            >
              <Tag className="w-4 h-4" />
              <span>Categories</span>
            </Link>
            
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="text-yellow-400 hover:text-yellow-300 transition-colors flex items-center space-x-1"
              >
                <Crown className="w-4 h-4" />
                <span>Admin</span>
              </Link>
            )}
            
            {user?.role === 'moderator' && (
              <Link
                to="/admin"
                data-testid="nav-moderator"
                className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-1"
              >
                <Crown className="w-4 h-4" />
                <span>Moderator</span>
              </Link>
            )}

            {user?.subscription_status !== 'active' && user?.role !== 'admin' && (
              <Link
                to="/subscription"
                data-testid="nav-subscription"
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                Upgrade to Pro
              </Link>
            )}

            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-muted-foreground hover:text-white transition-colors"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </button>
            <Link
              to="/settings"
              data-testid="nav-settings"
              className="text-muted-foreground hover:text-white transition-colors"
              title="Settings"
            >
              <Settings className="w-5 h-5" />
            </Link>
            <Link
              to="/support"
              data-testid="nav-support"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
              title="Support"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="text-sm">Support</span>
            </Link>
            <Link
              to="/how-to-use"
              data-testid="nav-help"
              className="text-muted-foreground hover:text-white transition-colors flex items-center space-x-1"
              title="How to Use"
            >
              <HelpCircle className="w-5 h-5" />
              <span className="text-sm">Help</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden backdrop-blur-xl bg-black/90 border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/dashboard"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              to="/transactions"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Transactions
            </Link>
            <Link
              to="/analytics"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Analytics & Reports
            </Link>
            <Link
              to="/invoices"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Invoices
            </Link>
            <Link
              to="/tax-calculator"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Tax Calculator
            </Link>
            <Link
              to="/smart-rules"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Smart Rules
            </Link>
            <Link
              to="/receipts-gallery"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Receipts
            </Link>
            <Link
              to="/budget-limits"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Budgets
            </Link>
            <Link
              to="/categories"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Categories
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="block text-yellow-400 hover:text-yellow-300 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Admin Panel
              </Link>
            )}
            {user?.subscription_status !== 'active' && user?.role !== 'admin' && (
              <Link
                to="/subscription"
                className="block text-indigo-400 hover:text-indigo-300 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Upgrade to Pro
              </Link>
            )}
            <button
              onClick={handleLogout}
              className="block w-full text-left text-red-400 hover:text-red-300 transition-colors"
            >
              Logout
            </button>
            <Link
              to="/settings"
              className="block text-white hover:text-indigo-400 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Settings
            </Link>
            <Link
              to="/support"
              className="block text-indigo-400 hover:text-indigo-300 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Support
            </Link>
            <Link
              to="/how-to-use"
              className="block text-indigo-400 hover:text-indigo-300 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              How to Use
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
